import { AjaxHelper } from '~/common/extensions/ajax_helpers'
import { processResponse } from '~/site/behaviors/remote/process_response'
import { hideLoading, proceedWithPayment, showLoading } from '../common'
import { vatCheckPending } from '../vat_check_lock'
import { redirectToLogin } from './login_redirect'
import {
  appendCheckoutCaptchaToken,
  resetCheckoutCaptcha,
} from './checkout_captcha'

// Submits the form to our servers and handles the response ensuring form validity and pre-purchase contracts are signed.
// Either our server is successful in charging/setting up intents, in which case, we call onSuccess
//   onSuccess will contain all processor-specific logic.
// Or our server is unsuccessful, in which case we call onError
//   We also run the JSON through processResponse in case of failures which will handle generic validation errors
export const processFormSubmission = async (
  form,
  { getFormData, onSuccess, onError, expressCheckout = false, signal }
) => {
  let failure
  let exceptionMessage
  try {
    // The amount on screen can still change: a VIES lookup for the buyer's VAT number has not come
    // back, or a cart update is in flight. Paying now can charge VAT the buyer does not owe, since
    // reverse charge takes it to zero.
    //
    // This is the chokepoint, not the button. Every processor reaches payment through here — the
    // ordinary submit, the wallets, and the per-processor click handlers delegated on document,
    // which never look at defaultPrevented and so cannot be stopped by the guard on the button
    // alone. Refusing here is what makes the lock hold for all of them, including any processor
    // added later.
    if (vatCheckPending()) {
      hideLoading()
      return null
    }

    // Make sure form is valid and pre-purchase contracts are signed
    if (!(await proceedWithPayment(form, { expressCheckout }))) {
      hideLoading()
      return null
    }

    // Get the form data to submit
    const formData = appendCheckoutCaptchaToken(
      getFormData?.(form) || new FormData(form)
    )

    // Show the loader.
    // Note: Don't call hideLoading in a finally block.
    //       Because onSuccess/onError might want to do other async things and show/hide loaders itself.
    showLoading()

    const response = await fetch(form.getAttribute('action'), {
      method: 'POST',
      body: formData,
      signal,
      headers: {
        Accept: 'application/javascript',
      },
    })

    let json = null

    if (!response.ok) {
      let errorMsg = 'Received error code: ' + response.status

      try {
        json = await response.json()
        // An existing user who is not logged in is sent to login instead of seeing a generic error.
        if (redirectToLogin(json)) return null
        if (json?.request_id) {
          errorMsg += ` If you contact support you can give them this request ID: ${json.request_id}`
        }
      } catch {
        // Nothing to do
      }

      throw errorMsg
    }

    json = await response.json()

    // redirect_url is a payment still in flight — the buyer approves with their provider and comes
    // back through the transaction's return_url — not a failure, so it routes to onSuccess too.
    if (json.success || json.attempt_3ds || json.redirect_url) {
      // No need to hide loading - success callbacks usually redirect to a different page.
      // If there's an error when submitting to the processor, the caller is responsible for hiding the loader
      return await onSuccess(json)
    } else {
      // Generic error handlers
      if (expressCheckout) {
        // Keep the wallet and payment state intact. The server may still render
        // its validation messages in the checkout's existing error container.
        const errors = json?.replace?.['cart-errors-container']
        if (typeof errors === 'string')
          processResponse({ replace: { 'cart-errors-container': errors } })
      } else if (json?.use_ajax_helper && window.AjaxHelper) {
        AjaxHelper.doUpdate(json)
      } else {
        processResponse(json)
      }
      if (json?.replace && json.replace['cart-errors-container']) {
        document.getElementById('cart-errors-container')?.scrollIntoView()
      }

      failure = json
    }
  } catch (e) {
    const message = e?.message || String(e)
    failure = { success: false, error: message, message }
    exceptionMessage = 'Sorry, we could not complete your request. ' + e
  }

  hideLoading()
  // A server-inserted widget is fresh; an existing one holds a token that is now spent.
  if (!failure?.captcha_required) resetCheckoutCaptcha()
  if (exceptionMessage && !expressCheckout) alert(exceptionMessage)
  await onError?.(failure)
  return null
}
